"use client";

import { ClipboardList, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import ReferralCardEditable from "@/src/components/cards/referral-card/referral-card-editable";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import {
  referralStatusIcons,
  defaultReferralStatusIconAndColor,
  schedulingPriorityIcons,
} from "@/src/components/tasks/model-icons";
import { useUser } from "@/src/context/user-context";
import { usePriorAuthorizationsByReferralId } from "@/src/lib/data-fetchers/prior-authorization-fetchers";
import { canWriteSOR } from "@/src/lib/rbac";
import { formatFaxParens, formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import {
  isStatOrUrgentPriority,
  Referral,
  ReferralStatus,
  SchedulingPriority,
  referralStatusLabel,
} from "@/src/models/documents/referral";
import { getPatientName } from "@/src/models/patient";

export default function ReferralCard({ referral, isEditable = false }: { referral: Referral; isEditable?: boolean }) {
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { roles } = useUser();
  const canWriteSor = canWriteSOR(roles);
  const effectiveIsEditable = isEditable;
  const { priorAuthorizations } = usePriorAuthorizationsByReferralId(referral?.id ?? null);
  const authExpirationDate = priorAuthorizations.find((pa) => pa.endDate)?.endDate ?? null;

  if (!referral) {
    return null;
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return <ReferralCardEditable referral={referral} onClose={handleEditToggle} />;
  }

  const patientDisplayId = referral.patient?.displayId;
  const patientName = referral.patient ? getPatientName(referral.patient) : null;

  const customActions: { label: string; onClick: () => void }[] = [
    ...(canWriteSor ? [{ label: "Edit Referral", onClick: handleEditToggle }] : []),
    ...(patientDisplayId
      ? [{ label: "View Patient", onClick: () => router.push(`/patients/${patientDisplayId}`) }]
      : []),
  ];

  const cardActionsProps = createCardActionsProps({
    id: referral.id,
    label: "Referral ID",
    href: `/referrals/${referral.displayId}`,
    customProps: customActions,
  });

  const diagnosisList = Array.isArray(referral.diagnosisList) ? referral.diagnosisList : [];
  const hasDiagnoses = diagnosisList.length > 0 || Boolean(referral.diagnosisCode || referral.diagnosisText);
  const status = referral.status as ReferralStatus | null;
  const statusMeta = referralStatusIcons[status || ReferralStatus.NOT_STARTED] || defaultReferralStatusIconAndColor;
  const StatusIcon = statusMeta?.icon;
  const schedulingPriority = referral.schedulingPriority as SchedulingPriority | null;
  const schedulingPriorityMeta = schedulingPriority ? schedulingPriorityIcons[schedulingPriority] : null;
  const SchedulingPriorityIcon = schedulingPriorityMeta?.icon;
  const showSchedulingPriorityFlag = isStatOrUrgentPriority(schedulingPriority);
  const referringFax = formatFaxParens(referral.referringLocation?.faxPhoneNumber);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-center">
              <ClipboardList className="h-6 w-6 mr-2" />
              <CardTitle className="text-md font-semibold">Referral Information</CardTitle>
              {showSchedulingPriorityFlag && SchedulingPriorityIcon && (
                <SchedulingPriorityIcon
                  weight="fill"
                  className={`ml-3 h-4 w-4 ${schedulingPriorityMeta?.color ?? ""}`}
                />
              )}
              <div className="ml-auto flex items-center gap-2">
                {!pathname?.startsWith(`/referrals/${referral.displayId}`) ? (
                  <Link
                    className="text-muted-foreground hover:text-foreground"
                    href={`/referrals/${referral.displayId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SquareArrowOutUpRight className="size-4" />
                  </Link>
                ) : null}
                {effectiveIsEditable && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <CardActions {...cardActionsProps} isContextMenu={false} />
                  </DropdownMenu>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {patientName && (
                <div className="space-y-1">
                  <span className="font-semibold block">Patient</span>
                  <CopyableContent text={patientName} />
                </div>
              )}

              <div className="space-y-1">
                <span className="font-semibold block">Status</span>
                <div className="flex items-center gap-2">
                  {StatusIcon && <StatusIcon weight="fill" className={`h-4 w-4 ${statusMeta?.color ?? ""}`} />}
                  <span className="no-select">{referralStatusLabel(status || ReferralStatus.NOT_STARTED)}</span>
                </div>
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Date of Referral</span>
                <CopyableContent text={referral.dateOfReferral ? formatIsoDate(referral.dateOfReferral) : "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Date Received</span>
                <CopyableContent text={referral.dateReceived ? formatIsoDate(referral.dateReceived) : "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Service Line</span>
                <CopyableContent text={referral.serviceLine || "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Authorization Number</span>
                <CopyableContent text={referral.authorizationNumber || "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Authorization Expiration Date</span>
                <CopyableContent text={authExpirationDate ? formatIsoDate(authExpirationDate) : "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Reason for Referral</span>
                <CopyableContent text={referral.reasonForReferral || "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Scheduling Priority</span>
                <CopyableContent text={referral.schedulingPriority || "N/A"} />
              </div>

              <div className="space-y-1">
                <span className="font-semibold block">Referring Location Fax</span>
                <CopyableContent text={referringFax} />
              </div>

              {!!referral.section?.reviewedContent?.isVaMilitaryReferral && (
                <div className="col-span-3 space-y-1">
                  <Badge className="bg-yellow-500 text-white dark:bg-yellow-600">VA / Military / ADSM</Badge>
                </div>
              )}
            </div>
            {hasDiagnoses && (
              <div className="mt-6 space-y-2">
                <span className="font-semibold block text-sm">ICD-10 Diagnosis List</span>
                <DiagnosisListTable
                  diagnoses={diagnosisList}
                  fallbackDiagnosis={
                    referral.diagnosisCode || referral.diagnosisText
                      ? {
                          diagnosisCode: referral.diagnosisCode ?? null,
                          diagnosisText: referral.diagnosisText ?? null,
                        }
                      : null
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>
      </ContextMenuTrigger>
      {effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}
